import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSupplier, updateSupplier } from '@/services/suppliers';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Supplier } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Save, Loader2 } from 'lucide-react';

const supplierSchema = z.object({
  name: z.string().min(2, 'Supplier name must be at least 2 characters').max(150),
  contact_name: z.string().max(120).optional().or(z.literal('')),
  phone: z
    .string()
    .max(30, 'Phone number too long')
    .refine(
      (val) => val === '' || /^[+\d\s\-()]+$/.test(val),
      'Invalid phone number format'
    )
    .optional()
    .or(z.literal('')),
  email: z.string().email('Invalid email address').max(120).optional().or(z.literal('')),
  address: z.string().max(300).optional().or(z.literal('')),
  notes: z.string().max(1000).optional().or(z.literal('')),
});

type SupplierFormData = z.infer<typeof supplierSchema>;

interface SupplierFormProps {
  supplier?: Supplier;
  initialName?: string;
  onSuccess: (createdSupplier?: Supplier) => void;
  onCancel: () => void;
}

function toStr(val: string | null | undefined): string {
  return val ?? '';
}

export const SupplierForm: React.FC<SupplierFormProps> = ({
  supplier,
  initialName,
  onSuccess,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const isEdit = !!supplier;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      name: toStr(supplier?.name) || initialName || '',
      contact_name: toStr(supplier?.contact_name),
      phone: toStr(supplier?.phone),
      email: toStr(supplier?.email),
      address: toStr(supplier?.address),
      notes: toStr(supplier?.notes),
    },
  });

  const mutation = useMutation({
    mutationFn: (data: SupplierFormData) => {
      const payload = {
        name: data.name,
        contact_name: data.contact_name || null,
        phone: data.phone || null,
        email: data.email || null,
        address: data.address || null,
        notes: data.notes || null,
      };
      return isEdit
        ? updateSupplier(supplier.id, payload)
        : createSupplier(payload);
    },
    onSuccess: (savedSupplier) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      onSuccess(savedSupplier);
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const onSubmit = (data: SupplierFormData) => {
    setSubmitError(null);
    mutation.mutate(data);
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2 bg-brand-bg border rounded-md text-sm text-brand-black placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all ${
      hasError ? 'border-brand-red focus:ring-brand-red' : 'border-brand-border'
    }`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {submitError && <ErrorAlert message={submitError} />}

      {/* Supplier Name */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Supplier / Company Name <span className="text-brand-red">*</span>
        </label>
        <input
          {...register('name')}
          placeholder="Tech Distributors Pvt. Ltd."
          className={inputClass(!!errors.name)}
        />
        {errors.name && (
          <p className="mt-1 text-xs text-brand-red">{errors.name.message}</p>
        )}
      </div>

      {/* Contact Name */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Contact Person
        </label>
        <input
          {...register('contact_name')}
          placeholder="Mr. Tariq Hussain"
          className={inputClass(!!errors.contact_name)}
        />
        {errors.contact_name && (
          <p className="mt-1 text-xs text-brand-red">{errors.contact_name.message}</p>
        )}
      </div>

      {/* Phone & Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Phone
          </label>
          <input
            {...register('phone')}
            placeholder="+92 300 1234567"
            className={inputClass(!!errors.phone)}
          />
          {errors.phone && (
            <p className="mt-1 text-xs text-brand-red">{errors.phone.message}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Email
          </label>
          <input
            {...register('email')}
            type="email"
            placeholder="supplier@company.com"
            className={inputClass(!!errors.email)}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-brand-red">{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* Address */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Address
        </label>
        <textarea
          {...register('address')}
          rows={2}
          placeholder="Warehouse No. 12, Industrial Area, Karachi"
          className={`${inputClass(!!errors.address)} resize-none`}
        />
        {errors.address && (
          <p className="mt-1 text-xs text-brand-red">{errors.address.message}</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Notes
        </label>
        <textarea
          {...register('notes')}
          rows={3}
          placeholder="Payment terms, delivery schedule, etc."
          className={`${inputClass(!!errors.notes)} resize-none`}
        />
        {errors.notes && (
          <p className="mt-1 text-xs text-brand-red">{errors.notes.message}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-2 border-t border-brand-border">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending || (isEdit && !isDirty)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{mutation.isPending ? 'Saving...' : isEdit ? 'Update Supplier' : 'Add Supplier'}</span>
        </button>
      </div>
    </form>
  );
};
